import {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import type {SessionUser} from "@corporatesclub/shared";
import {clearSessionCache, fetchSessionFresh, prefetchHomeData, readSessionCache, writeSessionCache} from "@/lib/shopCache";
import {useActivityRefresh} from "@/hooks/useActivityRefresh";

type SessionCtx = {
  user: SessionUser | null;
  ready: boolean;
  sessionChecked: boolean;
  refresh: () => Promise<SessionUser>;
  setCartCount: (n: number) => void;
  setNotifCount: (n: number) => void;
  patchUser: (patch: Partial<SessionUser>) => void;
};

const Ctx = createContext<SessionCtx>({
  user: null,
  ready: false,
  sessionChecked: false,
  refresh: async() => ({authenticated: false}),
  setCartCount: () => {},
  setNotifCount: () => {},
  patchUser: () => {}
});

export function SessionProvider({children}: {children: React.ReactNode}) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [ready, setReady] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const applyUser = useCallback((u: SessionUser) => {
    setUser(u);
    if(u.authenticated) writeSessionCache(u);
    else clearSessionCache();
  }, []);
  const refresh = useCallback(async() => {
    const u = await fetchSessionFresh();
    applyUser(u);
    setSessionChecked(true);
    setReady(true);
    return u;
  }, [applyUser]);
  useEffect(() => {
    const cached = readSessionCache();
    if(cached?.authenticated) {
      setUser(cached);
      setReady(true);
    }
    refresh().catch(() => {
      if(!readSessionCache()?.authenticated) applyUser({authenticated: false});
      setSessionChecked(true);
      setReady(true);
    });
  }, [applyUser, refresh]);
  useActivityRefresh(() => { refresh().catch(() => {}); }, !!user?.authenticated);
  useEffect(() => {
    if(!ready) return;
    prefetchHomeData();
  }, [ready]);
  useEffect(() => {
    function onCart(e: Event) {
      const n = (e as CustomEvent).detail;
      if(typeof n !== "number") return;
      setUser((u) => {
        if(!u) return u;
        const next = {...u, cartCount: n};
        writeSessionCache(next);
        return next;
      });
    }
    window.addEventListener("cart-updated", onCart);
    return () => window.removeEventListener("cart-updated", onCart);
  }, []);
  const setCartCount = useCallback((n: number) => {
    setUser((u) => {
      if(!u) return u;
      const next = {...u, cartCount: n};
      writeSessionCache(next);
      return next;
    });
  }, []);
  const setNotifCount = useCallback((n: number) => {
    setUser((u) => {
      if(!u) return u;
      const next = {...u, notificationCount: n};
      writeSessionCache(next);
      return next;
    });
  }, []);
  const patchUser = useCallback((patch: Partial<SessionUser>) => {
    setUser((u) => {
      if(!u) return u;
      const next = {...u, ...patch};
      writeSessionCache(next);
      return next;
    });
  }, []);
  const value = useMemo(() => ({user, ready, sessionChecked, refresh, setCartCount, setNotifCount, patchUser}), [user, ready, sessionChecked, refresh, setCartCount, setNotifCount, patchUser]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession() {
  return useContext(Ctx);
}
